import { useEffect, useMemo, useState } from 'react'
import { InfoTip } from '@/shared/ui/InfoTip'
import { nf } from '@/shared/util/numbers'
import type { Lead, PipelineOutcome, PipelineStage } from '@/types/crm'
import './SeatsByStage.css'

/**
 * How many seats sit at each stage of the pipeline.
 *
 * Companies won answers "how many deals"; this answers "how big". A book of
 * forty two-seat guiding outfits and a book of forty forty-seat operators are
 * the same number of leads and two different businesses, and until now nothing
 * on this screen could tell them apart — `teamSize` is free text carrying
 * "Multi-guide", and `enrichment.teamSize` is a harvested guess at headcount.
 * `Lead.seatsExpected` is the rep's own figure, and this is the view of it.
 *
 * ## Coverage is shown as a number, not explained in a sentence
 *
 * Each row carries a `sized/total` pair: how many of that stage's leads have a
 * seat count, out of how many are there. A paragraph under the bars used to say
 * the same thing in words and was removed on 4 Sep 2026 — the pair already says
 * it, and prose restating the data next to the data is the kind of thing a
 * reader learns to skip.
 *
 * ## Form
 *
 * Horizontal bars, one per stage, in pipeline order rather than sorted by size:
 * the order *is* the funnel, and re-sorting it by value would throw away the
 * one thing the sequence says. Every row carries its own label and its own
 * number, so identity and value both survive without colour — which matters
 * here more than usual, because the stage palette is not colourblind-safe
 * (`#e63333` Lost against `#3aa52f` Won measures ΔE 1.9 under deuteranopia, far
 * below the ΔE 8 those two would need to be told apart). Colour is
 * reinforcement so the bars match the stage badges in the table below; the
 * label is what carries the identity.
 *
 * ## What is left out
 *
 * **Do not contact and Archived**, since 4 Sep 2026. Both are decisions to
 * stop, and seats behind a company nobody may approach are not a number to put
 * on a chart about the size of the pipeline — a book with a big archive would
 * have shown a big bar for it. They are dropped from the bars, not merely from
 * the headline figure.
 *
 * `unverified` is not one of them, despite also being a held-back stage. It is a
 * queue rather than a rejection: those leads have not been judged yet, and 894
 * of the 1,133 seats on the book sit there. Hiding that would be hiding the
 * largest thing on the screen. It stays as a bar and stays out of the headline.
 *
 * Stages with no leads at all are left out too. A stage with leads but no seats
 * recorded stays, showing an empty bar — that is a real finding about the book,
 * not an absence of data to render.
 *
 * ## Collapsible, and it remembers
 *
 * The table is what this screen is for, and on a laptop this card plus the
 * toolbar left room for about five rows. Collapsing it leaves the headline
 * figure — the one number worth keeping visible — and gives the bars' height
 * back to the list.
 *
 * The choice is stored per browser rather than per session. A rep who wants the
 * space wants it on every visit, and a card that reopened on each reload would
 * have to be closed again every morning, which is the same as not being
 * collapsible.
 */
/** Per-rep preference, like the column choice next to it. */
const OPEN_KEY = 'skidaCrm.seatsByStageOpen'

function loadOpen(): boolean {
  try {
    // Open unless it was explicitly closed: a first-time reader should see the
    // card at least once, or they will never know the figure exists.
    return localStorage.getItem(OPEN_KEY) !== 'false'
  } catch {
    return true
  }
}

/**
 * Stages that mean "we are not doing this".
 *
 * Keyed rather than derived from `hidden`, which would also catch `unverified`
 * — see the header. Both are built-in keys the Cloud Functions reference, so
 * they cannot be renamed out from under this.
 */
const DISCARDED = new Set(['do-not-contact', 'archived'])

export function SeatsByStage({
  leads,
  stages,
}: {
  leads: Lead[]
  stages: (PipelineStage | PipelineOutcome)[]
}) {
  const [open, setOpen] = useState(loadOpen)

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_KEY, String(open))
    } catch {
      // A browser refusing storage is not a reason to fail to render a chart.
    }
  }, [open])

  const model = useMemo(() => {
    const seats = new Map<string, number>()
    const count = new Map<string, number>()
    const sized = new Map<string, number>()

    for (const l of leads) {
      count.set(l.stage, (count.get(l.stage) ?? 0) + 1)
      // Zero means "nobody has said yet", so it is not summed and not counted
      // as sized — see `Lead.seatsExpected`.
      if (l.seatsExpected > 0) {
        seats.set(l.stage, (seats.get(l.stage) ?? 0) + l.seatsExpected)
        sized.set(l.stage, (sized.get(l.stage) ?? 0) + 1)
      }
    }

    const rows = stages
      .filter((s) => !DISCARDED.has(s.key) && (count.get(s.key) ?? 0) > 0)
      .map((s) => ({
        key: s.key,
        label: s.label,
        color: s.color,
        seats: seats.get(s.key) ?? 0,
        leads: count.get(s.key) ?? 0,
        sized: sized.get(s.key) ?? 0,
        /**
         * Drawn, but not counted in the headline.
         *
         * `won` and `lost` are decided; `hidden` is what remains of the
         * held-back stages once the discarded two are filtered out, which is
         * `unverified` — a queue of leads nobody has judged. Adding any of them
         * to "seats in the open pipeline" would make it larger than anything
         * anybody is actually working.
         */
        closed: s.key === 'won' || s.key === 'lost' || ('hidden' in s && s.hidden === true),
      }))

    /** The scale is set by the biggest bar drawn, so the shape uses the width. */
    const max = Math.max(1, ...rows.map((r) => r.seats))
    const open = rows.filter((r) => !r.closed)

    return {
      rows: rows.map((r) => ({ ...r, pct: Math.round((r.seats / max) * 100) })),
      openSeats: open.reduce((n, r) => n + r.seats, 0),
    }
  }, [leads, stages])

  return (
    <section className={`card seatstage${open ? '' : ' seatstage--closed'}`}>
      <div className="seatstage__head">
        <button
          className="seatstage__toggle"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          title={open ? 'Collapse — more room for the table' : 'Show seats by stage'}
        >
          <span className={`seatstage__chev${open ? ' seatstage__chev--open' : ''}`} aria-hidden="true">
            ▾
          </span>
          <span className="card-label seatstage__cardlabel">Seats by stage</span>
        </button>
        <div className="seatstage__total">
          <span className="seatstage__figure">{nf(model.openSeats)}</span>
          <span className="seatstage__unit">
            seats in the open pipeline
            <InfoTip label="What is counted">
              <p>
                Every seat a rep has typed onto a lead, summed per stage. Won and lost are shown
                but kept out of this figure, as are the held-back stages — a decided deal and a
                queue of unverified imports are not pipeline.
              </p>
              <p>
                This is what we expect to sell, not what anybody has bought.{' '}
                <strong>Seats bought</strong> is a separate column, mirrored from Skida PRO, and
                only exists once a company is paying.
              </p>
            </InfoTip>
          </span>
        </div>
      </div>

      {/* Collapsed keeps the headline above and drops the bars, the coverage
          line and the empty note — everything whose value is in its height. */}
      {/* `rows.length`, not the lead count: a book whose every company is
          archived has leads and nothing to draw. */}
      {!open ? null : model.rows.length === 0 ? (
        <div className="empty-note">Nothing in the pipeline to size yet.</div>
      ) : (
        <>
          <div className="seatstage__bars">
            {model.rows.map((r) => (
              <div
                key={r.key}
                title={
                  r.sized === 0
                    ? `${r.label} — ${r.leads} lead${r.leads === 1 ? '' : 's'}, none sized yet`
                    : `${r.label} — ${nf(r.seats)} seats across ${r.sized} of ${r.leads} lead${
                        r.leads === 1 ? '' : 's'
                      }`
                }
              >
                <div className="seatstage__barhead">
                  <span className="seatstage__barlabel">
                    {/* The stage's colour beside the name rather than on it:
                        a light hue is illegible as text, and the swatch is what
                        ties the row to the badge in the table below. */}
                    <span className="seatstage__swatch" style={{ background: r.color }} />
                    {/* Its own element so it can ellipsis. "Proposal /
                        Downloaded" beside a four-digit figure has nowhere to go
                        on a narrow pane, and a clipped label is worse than a
                        shortened one — the `title` above carries the full text. */}
                    <span className="seatstage__barname">{r.label}</span>
                  </span>
                  <span className="seatstage__barvalue">
                    {r.seats > 0 ? nf(r.seats) : <span className="muted">—</span>}
                    <span className="seatstage__barleads">
                      {r.sized}/{r.leads}
                    </span>
                  </span>
                </div>
                <div className="seatstage__bartrack">
                  <div
                    className="seatstage__barfill"
                    style={{ background: r.color, width: `${r.pct}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
